import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/locale";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, Users as UsersIcon, Lock, Globe } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", amount: "", currency: "AUD", cycle: "monthly",
  next_renewal_date: "", category: "Streaming", notes: "",
  logo_url: "", shared_with_household: true,
};

function CycleChip({ cycle }) {
  const label = cycle === "annual" ? "yearly" : cycle;
  return (
    <span className="chip bg-hearth-slate/12 text-hearth-slate inline-flex items-center gap-1 uppercase tracking-wider text-[10px]">
      {label}
    </span>
  );
}

export default function OnlineSubscriptions() {
  const [state, setState] = useState({ rows: [], monthly_total: 0, count: 0, categories: [], cycles: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // id if editing
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/online-subscriptions");
      setState(data);
    } catch (e) {
      console.error("Failed to load subscriptions", e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const startEdit = (row) => {
    setEditing(row.id);
    setForm({
      name: row.name || "",
      amount: row.amount ?? "",
      currency: row.currency || "AUD",
      cycle: row.cycle || "monthly",
      next_renewal_date: row.next_renewal_date || "",
      category: row.category || "Other",
      notes: row.notes || "",
      logo_url: row.logo_url || "",
      shared_with_household: !!row.shared_with_household,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("A name helps you find it later."); return; }
    const amt = parseFloat(form.amount);
    if (Number.isNaN(amt) || amt < 0) { toast.error("Amount needs to be a positive number."); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        amount: amt,
        currency: form.currency || "AUD",
        cycle: form.cycle,
        next_renewal_date: form.next_renewal_date || null,
        category: form.category || "Other",
        notes: form.notes || null,
        logo_url: form.logo_url || null,
        shared_with_household: !!form.shared_with_household,
      };
      if (editing) {
        await api.put(`/online-subscriptions/${editing}`, payload);
        toast.success("Subscription updated.");
      } else {
        await api.post("/online-subscriptions", payload);
        toast.success("Subscription tracked.");
      }
      setOpen(false); setEditing(null); setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save that just now.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (row.is_system) return;
    if (!window.confirm(`Remove ${row.name}?`)) return;
    try {
      await api.delete(`/online-subscriptions/${row.id}`);
      toast.success("Removed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't remove that.");
    }
  };

  const rowsSorted = useMemo(() => {
    return [...state.rows].sort((a, b) => (b.monthly_cost || 0) - (a.monthly_cost || 0));
  }, [state.rows]);

  return (
    <section id="online-subscriptions" className="warm-card p-5 mt-6" data-testid="online-subs-section">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="caption inline-flex items-center gap-1.5"><Globe size={12} /> Online subscriptions</p>
          <p className="text-hearth-mute text-xs mt-0.5 italic">
            Netflix, Spotify, iCloud, HearthHouse itself — all the recurring digital costs, in one place.
          </p>
        </div>
        <div className="flex items-baseline gap-3">
          <div className="text-right">
            <p className="stat-num text-hearth-slate num" data-testid="online-subs-monthly">{money(state.monthly_total)}</p>
            <p className="caption">per month · normalised</p>
          </div>
          <Button
            data-testid="online-subs-add"
            onClick={startCreate}
            className="rounded-full bg-hearth-ink hover:bg-hearth-slateDark text-white h-9 px-4"
          >
            <Plus size={14} className="mr-1.5" /> Add
          </Button>
        </div>
      </div>

      {rowsSorted.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-hearth-line px-6 py-8 text-center bg-hearth-cream/40">
          <p className="text-hearth-mute italic text-sm">No online subscriptions tracked yet — tap Add to record one.</p>
        </div>
      ) : (
        <div className="divide-y divide-hearth-line/70" data-testid="online-subs-list">
          {rowsSorted.map((row) => (
            <div
              key={row.id}
              data-testid={`online-sub-row-${row.id}`}
              className="flex items-center gap-3 py-3"
            >
              <div className="w-9 h-9 rounded-full bg-hearth-stone overflow-hidden shrink-0 grid place-items-center">
                {row.logo_url ? (
                  <img src={row.logo_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <span className="text-xs font-mono text-hearth-mute">
                    {(row.name || "?").slice(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-hearth-ink truncate">{row.name}</span>
                  <CycleChip cycle={row.cycle} />
                  {row.is_system && (
                    <span className="chip bg-hearth-amber/20 text-[#7A5320] inline-flex items-center gap-1 text-[10px]">
                      <Lock size={10} /> auto
                    </span>
                  )}
                  {row.shared_with_household && (
                    <span className="chip bg-hearth-sage/15 text-hearth-sageDark inline-flex items-center gap-1 text-[10px]">
                      <UsersIcon size={10} /> shared
                    </span>
                  )}
                </div>
                <div className="text-xs text-hearth-mute mt-0.5 flex gap-3 flex-wrap">
                  <span>{row.category}</span>
                  {row.next_renewal_date && <span>Renews {fmtDate(row.next_renewal_date)}</span>}
                  <span className="font-mono">≈ {money(row.monthly_cost)} / mo</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="font-mono text-hearth-ink">{money(row.amount)}</p>
                <p className="caption">/ {row.cycle === "annual" ? "year" : row.cycle === "weekly" ? "week" : "month"}</p>
              </div>
              <div className="flex items-center gap-1 ml-2">
                <button
                  data-testid={`online-sub-edit-${row.id}`}
                  onClick={() => startEdit(row)}
                  className="p-1.5 text-hearth-mute hover:text-hearth-ink"
                  title="Edit"
                >
                  <Pencil size={14} />
                </button>
                {!row.is_system && (
                  <button
                    data-testid={`online-sub-delete-${row.id}`}
                    onClick={() => remove(row)}
                    className="p-1.5 text-hearth-mute hover:text-hearth-coral"
                    title="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(EMPTY); } }}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-semibold">
              {editing ? "Edit subscription" : "Track a subscription"}
            </DialogTitle>
            <DialogDescription className="text-hearth-mute italic">
              Anything with a recurring bill — streaming, cloud storage, AI tools…
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="caption">Name</Label>
              <Input
                data-testid="online-sub-form-name"
                className="input-warm mt-1.5 h-10"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Netflix"
              />
            </div>
            <div>
              <Label className="caption">Amount</Label>
              <Input
                data-testid="online-sub-form-amount"
                type="number" step="0.01" min="0"
                className="input-warm mt-1.5 h-10 font-mono"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label className="caption">Cycle</Label>
              <Select value={form.cycle} onValueChange={(v) => setForm({ ...form, cycle: v })}>
                <SelectTrigger className="input-warm mt-1.5 h-10 rounded-xl" data-testid="online-sub-form-cycle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="caption">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="input-warm mt-1.5 h-10 rounded-xl" data-testid="online-sub-form-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {state.categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="caption">Next renewal</Label>
              <Input
                data-testid="online-sub-form-renewal"
                type="date"
                className="input-warm mt-1.5 h-10"
                value={form.next_renewal_date || ""}
                onChange={(e) => setForm({ ...form, next_renewal_date: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label className="caption">Logo URL (optional)</Label>
              <Input
                data-testid="online-sub-form-logo"
                className="input-warm mt-1.5 h-10"
                value={form.logo_url}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                placeholder="https://…"
              />
            </div>
            <div className="col-span-2">
              <Label className="caption">Notes</Label>
              <Textarea
                data-testid="online-sub-form-notes"
                className="input-warm mt-1.5 min-h-[70px]"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Family plan, shared with partner, etc."
              />
            </div>
            <label className="col-span-2 flex items-center gap-2 text-sm text-hearth-ink pt-1">
              <input
                data-testid="online-sub-form-shared"
                type="checkbox"
                checked={!!form.shared_with_household}
                onChange={(e) => setForm({ ...form, shared_with_household: e.target.checked })}
                className="accent-hearth-clay"
              />
              Shared across the household
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              data-testid="online-sub-form-save"
              onClick={save}
              disabled={busy}
              className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white"
            >
              {editing ? "Save changes" : "Track it"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
